import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectSelectedElement,
  selectSelectedTake3D,
  updateSelectedInteraction,
} from "../../../app/slices/TrainingSlice";
import {
  CreateEmotionBubble,
  IEmotionBubble,
} from "../../../models/interactions/EmotionBubble";
import {
  CreateMessageBubble,
  IMessageBubble,
  defaultMessageBubbleAnimation,
  MessageBubbleAnimation,
} from "../../../models/interactions/MessageBubble";
import { useTrainingLocalization } from "../../trainingLocalization/TrainingLocalizationHooks";
import { IConversation } from "./../../../models/interactions/Conversation";
import ConversationPreviewTextfield from "./ConversationPreviewTextfield";
import ConversationActorDropdown from "./ConversationActorDropdown";
import ConversationAnimationDropdown from "./ConversationAnimationDropdown";
import ConversationLookAtDropdown from "./ConversationLookAtDropdown";

export default function ConversationPreviewInput(props: {
  onSubmitClick: () => void;
}) {
  const conversation = useSelector(selectSelectedElement) as IConversation;
  const { getNewKey, changeValue } = useTrainingLocalization();
  const selectedTake = useSelector(selectSelectedTake3D);

  const dispatch = useDispatch();

  const [selectedObjectId, setSelectedObjectId] = useState("player");
  const [selectedLookAt, setSelectedLookAt] = useState(
    () =>
      selectedTake?.actors.find((actor) => actor.actorId === "player")?.lookat ??
      ""
  );
  const [selectedAnimation, setSelectedAnimation] = useState<MessageBubbleAnimation>(
    { ...defaultMessageBubbleAnimation }
  );
  const isActorAnchor = selectedTake?.actors.some(
    (actor) => actor.actorId === selectedObjectId
  );

  const onSubmit = (text: string) => {
    // request a new key
    var key = getNewKey();

    // fill in the key w/ entered text
    changeValue(key, text);

    const msg: IMessageBubble = CreateMessageBubble([key]);
    msg.npcId = selectedObjectId;
    msg.lookAt = isActorAnchor ? selectedLookAt : "";
    msg.animation = selectedAnimation;

    const cpy = { ...conversation };
    cpy.messages = [...cpy.messages, msg];
    props.onSubmitClick(); // callback to parent

    dispatch(updateSelectedInteraction(cpy));
  };

  const onAnchorChange = (objectId: string) => {
    setSelectedObjectId(objectId);

    const nextAnchorIsActor = selectedTake?.actors.some(
      (actor) => actor.actorId === objectId
    );
    if (!nextAnchorIsActor) setSelectedLookAt("");
  };

  const onEmotionSelect = (emotion: string) => {
    const msg: IEmotionBubble = CreateEmotionBubble();
    msg.npcId = selectedObjectId;
    msg.lookAt = isActorAnchor ? selectedLookAt : "";
    msg.messageBubbleStyle = emotion;
    const cpy = { ...conversation };
    cpy.messages = [...cpy.messages, msg];
    props.onSubmitClick();

    dispatch(updateSelectedInteraction(cpy));
  };

  return (
    <div className="min-h-[126px] flex flex-col border-[1.5px] border-solid border-interaction-primary rounded-[20px] gap-[10px] py-[12px] px-[16px]">
      <ConversationPreviewTextfield
        onSubmit={onSubmit}
        onEmotionSelect={onEmotionSelect}
      />
      <div className="flex flex-1 flex-wrap gap-[12px]">
        <div className="w-[156px]">
          <ConversationActorDropdown
            selectedObjectId={selectedObjectId}
            interactionId={conversation.id}
            onClick={onAnchorChange}
          />
        </div>
        <div className="w-[156px]">
          <ConversationAnimationDropdown
            selectedAnimation={selectedAnimation}
            setSelectedAnimation={(animation) =>
              setSelectedAnimation(animation)
            }
          />
        </div>
        <div className="w-[156px]">
          <ConversationLookAtDropdown
            selectedTargetActorId={selectedLookAt}
            onChange={setSelectedLookAt}
            disabled={!isActorAnchor}
            variant="input"
          />
        </div>
      </div>
    </div>
  );
}
